import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DRAFT_TOKEN_KEY, INTAKE_KEY, readSession, writeSession } from "@/lib/auth";
import { guessArchetype } from "@/lib/heuristics";
import { IntakePanel } from "@/components/iv/intake/IntakePanel";
import { CriteriaInspector } from "@/components/iv/intake/CriteriaInspector";
import { SignInModal } from "@/components/iv/intake/SignInModal";

const EMPTY = { idea_text: "", country_code: "", city_region: "", target_audience: "" };
const pick = (d) => ({ idea_text: (d.idea_text || "").slice(0, 1500), country_code: d.country_code || "", city_region: d.city_region || "", target_audience: d.target_audience || "" });
const errorText = (e) => {
  const detail = e.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return "Something went wrong. Your idea is still saved here — please try again.";
};

export const IntakeStage = () => {
  const { user, authChecked, refreshQuota, requireMobile } = useApp();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...EMPTY, ...pick(readSession(INTAKE_KEY) || {}) }));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const resumed = useRef(false);

  useEffect(() => writeSession(INTAKE_KEY, form), [form]);
  // A finished run belongs to the text it classified; editing or loading a preset clears it.
  useEffect(() => {
    if (result && form.idea_text.trim() !== result.idea.idea_text) setResult(null);
  }, [form.idea_text, result]);

  const runStart = useCallback(async (body) => {
    if (!(await requireMobile())) {
      setError("Add your mobile number to run your assessment. Your idea is still saved here.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/validations/start", body);
      setResult(data);
      sessionStorage.removeItem(DRAFT_TOKEN_KEY);
      refreshQuota();
      toast.success(data.status_message);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }, [refreshQuota, requireMobile]);

  useEffect(() => {
    if (params.get("resume") !== "1" || !authChecked || resumed.current) return;
    resumed.current = true;
    setParams((p) => { const q = new URLSearchParams(p); q.delete("resume"); return q; }, { replace: true });
    if (!user) {
      setError("Sign-in didn't complete. Your idea is still saved here — click Run to sign in again.");
      return;
    }
    (async () => {
      let restored = readSession(INTAKE_KEY);
      if (!restored?.idea_text) {
        restored = await api.get("/drafts/current").then((r) => r.data).catch(() => null);
        if (restored) setForm(pick(restored));
      }
      const token = readSession(DRAFT_TOKEN_KEY);
      await runStart({ ...(token ? { draft_token: token } : {}), ...(restored?.idea_text ? pick(restored) : {}) });
    })();
  }, [params, authChecked, user, setParams, runStart]);

  const submit = async () => {
    setResult(null);
    if (user) return runStart(pick(form));
    setBusy(true);
    setError(null);
    try {
      writeSession(INTAKE_KEY, form);
      const { data } = await api.post("/drafts", pick(form));
      writeSession(DRAFT_TOKEN_KEY, data.draft_token);
      setSignInOpen(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const detected = result?.classification?.archetype_key || null;
  return (
    <section className="py-8" aria-labelledby="intake-title" data-testid="stage-2-intake">
      <h1 id="intake-title" className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Idea &amp; Country Intake</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Describe your idea once. We save it in an encrypted vault before sign-in, so nothing is lost.</p>
      <div className="mt-6 grid gap-5 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-7">
          <IntakePanel form={form} setForm={setForm} onSubmit={submit} busy={busy} error={error} result={result} />
        </div>
        <div className="min-w-0 lg:col-span-5">
          <CriteriaInspector detected={detected} liveGuess={guessArchetype(form.idea_text)} />
        </div>
      </div>
      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
    </section>
  );
};
